import React, { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";
import styled from "styled-components";
import Draggable from "react-draggable";
import { STATIC_PATH } from "../constants/config";
import { PORTAL_ID } from "../constants/index";
import useMobileDetection from "../helper/hooks/useMobileDetection";

const OverlayContainer = styled.div`
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  background: ${(props) =>
    props.$overlayBg ||
    `0% 0% no-repeat padding-box padding-box ${props.theme.overlayBgColor}`};
  z-index: 9850;
  display: ${(props) => props.display};
  align-items: ${(props) => (props.$fullScreen ? "stretch" : "center")};
  justify-content: ${(props) => (props.$fullScreen ? "stretch" : "center")};
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  overscroll-behavior: contain;
  padding: ${(props) => (props.$fullScreen ? "0" : "16px")};
  box-sizing: border-box;

  @media (max-width: 767px) {
    * {
      touch-action: auto;
      -webkit-overflow-scrolling: touch;
    }
  }
`;

const Overlay = styled.div`
  z-index: 50;
  width: ${(props) => props.width || "384px"};
  height: ${(props) => props.height || "auto"};
  max-height: ${(props) => props.maxHeight || "unset"};
  position: ${(props) => (props.$fullScreen ? "relative" : "absolute")};
  top: ${(props) => (props.$fullScreen ? "0" : "50%")};
  left: ${(props) => (props.$fullScreen ? "0" : "50%")};
  margin-left: auto;
  margin-right: auto;
  background: ${(props) =>
    props.$fullScreen ? "transparent" : `${props.theme.polarColor} 0% 0% no-repeat padding-box`};
  border-radius: ${(props) => (props.$fullScreen ? "0" : props.borderRadius || "8px")};
  transform: ${(props) => (props.$fullScreen ? "none" : "translate(-50%, -50%)")} !important;
  padding: ${(props) => props.padding};
  text-align: ${(props) => props.align};
  overflow: ${(props) => props.overflow};
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  flex: ${(props) => (props.$fullScreen ? "1" : "none")};
  max-width: ${(props) => (props.$fullScreen ? "100%" : "unset")};

  @media (max-width: 768px) {
    max-width: ${(props) => (props.$fullScreen ? "100%" : "calc(100% - 0px)")};
    width: 100%;
    scroll-behavior: smooth;
    overflow-y: auto;
    touch-action: pan-y;
    * {
      touch-action: auto;
      -webkit-overflow-scrolling: touch;
    }
  }

  @media only screen and (min-width: 768px) and (max-width: 1189px) {
    overflow: ${(props) => props.mdOverflow || "auto"};
    overflow-x: hidden;
  }

  @media only screen and (max-width: 767px) {
    position: ${(props) => (props.$fullScreen ? "relative" : "relative")};
    top: ${(props) => (props.$fullScreen ? "0" : "auto")};
    left: ${(props) => (props.$fullScreen ? "0" : "auto")};
    width: 100%;
    height: ${(props) => (props.$fullScreen ? props.smHeight || "100%" : props.smHeight || "auto")};
    overflow: ${(props) => props.smOverflow || "auto"};
    overflow-x: hidden;
    transform: none !important;
    flex-shrink: ${(props) => (props.$fullScreen ? "1" : "0")};
    touch-action: pan-y;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    * {
      touch-action: auto;
      -webkit-overflow-scrolling: touch;
    }
  }
`;

const CloseIcon = styled.img`
  width: ${props => props.closeIconWidth || "17px"};
  height: ${props => props.closeIconHeight || "17px"};
  position: absolute;
  right: ${props => props.closeIconRight || "8px"};
  top: ${props => props.closeIconTop || "13px"};
  cursor: pointer;
  z-index: 999;
  touch-action: auto;
  filter: ${(props) => props.$closeIconFilter || "none"};
  ${(props) => props.$closeIconBg && `
    padding: 8px;
    border-radius: 50%;
    background-color: ${props.$closeIconBg};
    box-sizing: content-box;
  `}
  &:hover {
    transform: scale(1.1);
    background-color: ${(props) =>
    props.$closeIconBg
      ? props.$closeIconHoverBg || "rgba(255, 255, 255, 0.3)"
      : props.closeIconPath ? "#ddd" : "transparent"};
  }
  @media (max-width: 767px) {
    right: ${props => props.closeIconMobRight || "2%"};
    top: ${props => props.closeIconMobTop || "2%"};
  }
`;

const BackDrop = styled.div`
  position: fixed;
  inset: 0;
  height: 100%;
  width: 100%;
`;

const modalStack = [];

export const Modal = ({
  children,
  customStyles,
  isDisabled = true,
  showModal = false,
  width,
  height,
  smHeight,
  smOverflow,
  borderRadius,
  preventClose = false,
  preventEscClose = false,
  onClose = () => { },
  isRender = false,
  padding,
  align,
  showBackdrop = false,
  maxHeight,
  overflow,
  closeIconPath,
  closeIconWidth,
  closeIconHeight,
  closeIconRight,
  closeIconTop,
  closeIconMobRight,
  closeIconMobTop,
  mdOverflow,
  zIndex = 9850,
  fullScreen = false,
  overlayBg,
  closeIconBg,
  closeIconHoverBg,
  closeIconFilter,
}) => {
  const overlayContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const stackEntryRef = useRef(null);
  const isMobile = useMobileDetection();

  const resetScrollPosition = useCallback(() => {
    [overlayContainerRef, overlayRef].forEach((ref) => {
      if (ref.current) {
        ref.current.scrollTop = 0;
        ref.current.scrollLeft = 0;
      }
    });
  }, []);

  const handleClose = useCallback(() => {
    resetScrollPosition();
    onClose();
  }, [onClose, resetScrollPosition]);

  useEffect(() => {
    if (!showModal && !isRender) {
      return undefined;
    }

    const entry = {
      onClose: handleClose,
      preventClose,
      preventEscClose,
    };
    stackEntryRef.current = entry;
    modalStack.push(entry);

    return () => {
      const index = modalStack.indexOf(entry);
      if (index >= 0) {
        modalStack.splice(index, 1);
      }
      stackEntryRef.current = null;
    };
  }, [showModal, isRender, handleClose, preventClose, preventEscClose]);

  const onKeyDown = useCallback((e) => {
    const isEscape = e.key === "Escape" || e.keyCode === 27;
    if (!isEscape) return;

    const topModal = modalStack[modalStack.length - 1];
    if (!topModal || topModal !== stackEntryRef.current) {
      return;
    }

    if (topModal.preventClose || topModal.preventEscClose) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    topModal.onClose();
  }, []);

  useEffect(() => {
    if (!showModal && !isRender) {
      return undefined;
    }

    window.addEventListener("keydown", onKeyDown, true);

    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
    };
  }, [onKeyDown, showModal, isRender]);

  useEffect(() => {
    if (!showModal && !isRender) {
      return undefined;
    }

    resetScrollPosition();
    const rafId = requestAnimationFrame(resetScrollPosition);

    return () => {
      cancelAnimationFrame(rafId);
      resetScrollPosition();
    };
  }, [showModal, isRender, resetScrollPosition]);

  useEffect(() => {
    if (showModal || isRender) {
      resetScrollPosition();
    }
  }, [isMobile, showModal, isRender, resetScrollPosition]);

  const overlayPanel = (
    <Overlay
      ref={overlayRef}
      style={customStyles}
      $fullScreen={fullScreen}
      width={width}
      height={height}
      maxHeight={maxHeight}
      smHeight={smHeight}
      overflow={overflow}
      smOverflow={smOverflow}
      mdOverflow={mdOverflow}
      borderRadius={borderRadius}
      padding={padding}
      align={align}
    >
      {!preventClose && (
        <CloseIcon
          src={`${STATIC_PATH}${closeIconPath || "close-icon.svg"}`}
          alt="close"
          onClick={handleClose}
          draggable={false}
          closeIconWidth={closeIconWidth}
          closeIconHeight={closeIconHeight}
          closeIconRight={closeIconRight}
          closeIconTop={closeIconTop}
          closeIconMobRight={closeIconMobRight}
          closeIconMobTop={closeIconMobTop}
          $closeIconBg={closeIconBg}
          $closeIconHoverBg={closeIconHoverBg}
          $closeIconFilter={closeIconFilter}
        />
      )}
      {children}
    </Overlay>
  );

  const modalContent = (showModal || isRender) && (
    <OverlayContainer
      ref={overlayContainerRef}
      display={showModal ? "flex" : "none"}
      style={{ zIndex }}
      $fullScreen={fullScreen}
      $overlayBg={overlayBg}
    >
      {showBackdrop && <BackDrop onClick={handleClose} />}
      {fullScreen ? (
        overlayPanel
      ) : (
        <Draggable disabled={isDisabled || isMobile}>
          {overlayPanel}
        </Draggable>
      )}
    </OverlayContainer>
  );

  return ReactDOM.createPortal(
    modalContent,
    document.getElementById(PORTAL_ID)
  );
};

Modal.propTypes = {
  width: PropTypes.string,
  height: PropTypes.string,
  smOverflow: PropTypes.string,
  smHeight: PropTypes.string,
  maxHeight: PropTypes.string,
  borderRadius: PropTypes.string,
  padding: PropTypes.string,
  overflow: PropTypes.string,
  align: PropTypes.string,
  preventClose: PropTypes.bool,
  preventEscClose: PropTypes.bool,
  closeIconPath: PropTypes.string,
  closeIconWidth: PropTypes.string,
  closeIconHeight: PropTypes.string,
  closeIconRight: PropTypes.string,
  closeIconTop: PropTypes.string,
  closeIconMobRight: PropTypes.string,
  closeIconMobTop: PropTypes.string,
  mdOverflow: PropTypes.string,
  zIndex: PropTypes.number,
  fullScreen: PropTypes.bool,
  overlayBg: PropTypes.string,
  closeIconBg: PropTypes.string,
  closeIconHoverBg: PropTypes.string,
  closeIconFilter: PropTypes.string,
};
